import hasFeaturePermission from 'helpers/permission';
import { Feature } from 'helpers/utils';
import { Loader2 } from 'lucide-react';
import React, { ReactNode, useState } from 'react';

export type TButtonType = 'orange' | 'blue' | 'emerald' | 'red' | 'white';

interface ButtonBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  hiddenLoading?: boolean;
  children?: ReactNode;
  color?: TButtonType;
  privateClassName?: string;
}

type PermissionControlledProps =
  | {
      feature: Feature;
      permission: number;
    }
  | {
      feature?: undefined;
      permission?: undefined;
    };

type CommonButtonProps = ButtonBaseProps & PermissionControlledProps;

const Button: React.FC<CommonButtonProps> = ({
  children,
  className = 'px-4',
  onClick,
  color = 'blue',
  title,
  feature,
  permission = 0,
  hiddenLoading = true,
  privateClassName = '',
  ...rest
}) => {
  const role = hasFeaturePermission(feature, permission);

  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading) return;
    setLoading(true);

    try {
      if (onClick) {
        await onClick(e);
      }
    } catch (_e) {
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const isDisabled = loading || rest.disabled;

  const colorStyle = (() => {
    if (color === 'white') {
      return isDisabled
        ? `text-gray-400 bg-white border border-gray-200`
        : `text-gray-700 bg-white hover:bg-gray-100 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-500 border border-gray-300`;
    }

    return isDisabled
      ? `text-white bg-${color}-500 opacity-80`
      : `text-white bg-${color}-500 hover:bg-${color}-700`;
  })();

  if (feature && !role) return <></>;

  const classes =
    privateClassName ||
    `${title ? 'h-8' : 'h-10'} text-base min-w-max rounded-md font-medium transition flex items-center justify-center ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${colorStyle} ${loading ? 'opacity-70' : ''} ${className}`;

  return (
    <button
      type={rest.type || 'button'}
      {...rest}
      title={title}
      onClick={handleClick}
      disabled={isDisabled}
      className={classes}
    >
      {loading && !hiddenLoading ? (
        <span className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
